<script setup lang="ts">
/* useRoute() only syncs once NuxtPage renders, which waits on the guard below. */
const { currentRoute: route } = useRouter()
const { $auth0, $pendo } = useNuxtApp()
const config = useRuntimeConfig().public

const { user } = $auth0()

const workspaceId = config.WORKSPACE_ID as string
const appPermissionRequired = [WORKSPACE_PERMISSION.readPhi]

useHead(() => ({
  title: (route.value.meta.title as string) || undefined,
  titleTemplate: (s) =>
    s ? `${s} | Rialtic Provider Inquiries` : 'Rialtic Provider Inquiries',
}))

const {
  clear: clearUserData,
  data: userData,
  error: userDataError,
  execute: loadUserData,
  status,
} = useApiFetch('/api/auth-user', { immediate: false, watch: false })

/* The auth-user request needs a token, so it waits for the Auth0 session rather than failing on the login pages. */
watch(
  () => user.value?.sub,
  (sub) => {
    clearUserData()
    if (sub) loadUserData()
  },
  { immediate: true },
)

watch(
  user,
  (auth0User) => {
    if (!auth0User?.sub) return

    const { email, name: full_name } = auth0User
    const roles: string[] = auth0User['https://auth.rialtic.io/roles'] || []

    ;($pendo as any).identify({
      visitor: { id: email, email, full_name, role: roles[0] },
      account: {
        id: email?.split('@')[1] || workspaceId,
        rialtic_app: 'provider_inquiries',
        workspace_id: workspaceId,
      },
    })
  },
  { immediate: true },
)

const onLogout = useLogout()

const userPermissions = computed(() => userData.value?.permissions ?? [])
const workspace = computed(() => ({ ...userData.value?.workspace }))
const workspaceUnderMaintenance = computed(
  () => !!(workspace.value as any)?.system_document?.under_maintenance,
)

provide('user-permissions', userPermissions)
provide('workspace', workspace)
provide('workspace-under-maintenance', workspaceUnderMaintenance)

defineExpose({ onLogout })
</script>

<template>
  <UApp :tooltip="{ delayDuration: 0 }">
    <NuxtLoadingIndicator />
    <NuxtRouteAnnouncer />
    <AuthPermissionsGuard
      :workspace-id="workspaceId"
      :workspace-permissions="route.meta.public ? [] : appPermissionRequired"
    >
      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>

      <template #fallback>
        <div
          v-if="status === 'error'"
          class="flex min-h-screen items-center justify-center p-4"
          data-testid="app-error"
        >
          <AuthCard
            class="w-full sm:w-[400px]"
            :message="userDataError?.statusMessage || userDataError?.message"
            title="We couldn't load your account"
          >
            <div class="flex flex-col gap-3">
              <UButton class="justify-center" block @click="loadUserData()">
                Try again
              </UButton>
              <UButton
                class="justify-center"
                block
                color="neutral"
                variant="outline"
                @click="onLogout"
              >
                Log out
              </UButton>
            </div>
          </AuthCard>
        </div>
        <div
          v-else-if="!userData || status === 'pending'"
          class="flex min-h-screen items-center justify-center"
          data-testid="app-loading"
        >
          <RialticLogo class="size-24 animate-pulse" icon />
        </div>
        <PermissionDenied v-else @logout="onLogout" />
      </template>
    </AuthPermissionsGuard>
  </UApp>
</template>
